/**
 * WordPress Headless CMS Data Provider
 *
 * Uses two endpoints:
 *  POST /post   – collection CRUD  (type discriminates the WP post_type)
 *  GET|POST /option/{name} – key-value option groups
 *
 * Param translation:
 *  Supabase-style filter params are translated to WP API equivalents before
 *  being sent, e.g. is_published=true → status=publish.
 */

import type {
  DataProvider,
  GetListParams,
  GetListResult,
  GetOneParams,
  GetOneResult,
  CreateParams,
  CreateResult,
  UpdateParams,
  UpdateResult,
  DeleteParams,
  DeleteResult,
  DeleteManyParams,
  DeleteManyResult,
  UpdateManyParams,
  GetOptionResult,
  SetOptionResult,
} from './types';
import { PMS_API_ENDPOINT, WP_API_KEY, PMS_AUTH_USER, PMS_AUTH_KEY, PMS_SITE_ID } from './config';
import { toPostType, mapWpPostToRecord, mapRecordToWpPost } from './utils';

// ── Resource routing ─────────────────────────────────────────────────────────

/**
 * Resources that are stored as WP options (singleton key-value groups).
 * These route to /option/{name} instead of /post.
 */
const OPTION_RESOURCES = new Set([
  'business_settings',
  'business-settings',
  'businessSettings',
  'settings',
]);

/**
 * Resources that are stored as options BUT with a FLAT body (no meta wrapper).
 * The entire record is POSTed directly as the option value.
 */
const FLAT_OPTION_RESOURCES = new Set([
  'page_design_config',
  'pageDesignConfig',
  'page-design-config',
]);

// ── Param translation ─────────────────────────────────────────────────────────

/**
 * Translate Supabase-style filter params to WP REST API equivalents.
 *
 * Known mappings:
 *  is_published=true   → status=publish
 *  is_published=false  → status=draft
 *  is_active=false     → status=draft
 *  is_active=true      → status=publish
 */
const translateParams = (filter: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(filter)) {
    if (k === 'is_published' || k === 'is_active') {
      out['status'] = (v === true || v === 'true') ? 'publish' : 'draft';
    } else {
      out[k] = v;
    }
  }
  return out;
};

// ── Auth / headers ────────────────────────────────────────────────────────────

const headers = (): HeadersInit => {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };

  if (PMS_AUTH_USER && PMS_AUTH_KEY) {
    h['Authorization'] = `Basic ${btoa(`${PMS_AUTH_USER}:${PMS_AUTH_KEY}`)}`;
  } else if (WP_API_KEY) {
    h['Authorization'] = `Bearer ${WP_API_KEY}`;
  }

  if (PMS_SITE_ID) {
    h['X-PMS-Site-ID'] = PMS_SITE_ID;
  }

  return h;
};

// ── HTTP helper ───────────────────────────────────────────────────────────────

/** Sentinel error thrown when a WP option does not exist yet (needs seeding). */
export class WpOptionNotFoundError extends Error {
  constructor(public readonly optionName: string) {
    super(`WP option not found: ${optionName}`);
    this.name = 'WpOptionNotFoundError';
  }
}

const request = async <T>(url: string, init?: RequestInit): Promise<T> => {
  const res = await fetch(url, { ...init, headers: { ...headers(), ...init?.headers } });
  const body = await res.json().catch(() => null);

  // Detect WP "option not found" response — needs seeding from Supabase
  if (
    body &&
    typeof body === 'object' &&
    (body as Record<string, unknown>).success === false &&
    (body as Record<string, unknown>).code === 'not_found'
  ) {
    const msg = (body as Record<string, unknown>).message as string | undefined;
    const match = msg?.match(/Option '([^']+)' not found/);
    throw new WpOptionNotFoundError(match?.[1] ?? 'unknown');
  }

  if (!res.ok) {
    throw new Error(`WP API ${res.status}: ${JSON.stringify(body)}`);
  }
  return body as T;
};

// ── Data Provider ─────────────────────────────────────────────────────────────

export const wpDataProvider: DataProvider = {

  async getList<T = Record<string, unknown>>(
    resource: string,
    params?: GetListParams,
  ): Promise<GetListResult<T>> {
    // Flat option resources (e.g. page_design_config)
    if (FLAT_OPTION_RESOURCES.has(resource)) {
      const raw = await request<T>(`${PMS_API_ENDPOINT}/option/${toPostType(resource)}`);
      return { data: raw ? [raw] : [], total: raw ? 1 : 0 };
    }

    // Standard option resources (e.g. business_settings)
    if (OPTION_RESOURCES.has(resource)) {
      const raw = await request<T>(`${PMS_API_ENDPOINT}/option/business_setting`);
      return { data: raw ? [raw] : [], total: raw ? 1 : 0 };
    }

    const postType = toPostType(resource);
    const qs = new URLSearchParams({ type: postType });

    if (params?.pagination) {
      qs.set('per_page', String(params.pagination.perPage));
      qs.set('page', String(params.pagination.page));
    }
    if (params?.sort) {
      qs.set('orderby', params.sort.field);
      qs.set('order', params.sort.order.toLowerCase());
    }
    if (params?.filter) {
      const translated = translateParams(params.filter as Record<string, unknown>);
      for (const [k, v] of Object.entries(translated)) {
        if (v !== undefined && v !== null) qs.set(k, String(v));
      }
    }
    if (params?.meta) {
      for (const [k, v] of Object.entries(params.meta)) {
        if (v !== undefined && v !== null) qs.set(k, String(v));
      }
    }

    const url = `${PMS_API_ENDPOINT}/post?${qs.toString()}`;
    const raw = await request<any>(url);

    // Standard PMS DTO: { success, data: { items, total, pages } }
    if (raw?.data?.items && Array.isArray(raw.data.items)) {
      return {
        data: raw.data.items.map((r: Record<string, unknown>) => mapWpPostToRecord(r) as T),
        total: raw.data.total ?? raw.data.items.length,
      };
    }

    // Fallback: plain array
    if (Array.isArray(raw)) {
      return {
        data: raw.map((r: Record<string, unknown>) => mapWpPostToRecord(r) as T),
        total: raw.length,
      };
    }

    // Fallback: { data, total } envelope without items
    const envelope = raw as { data: unknown[]; total: number };
    return {
      data: (envelope.data ?? []).map((r) => mapWpPostToRecord(r as Record<string, unknown>) as T),
      total: envelope.total ?? envelope.data?.length ?? 0,
    };
  },

  async getOne<T = Record<string, unknown>>(
    resource: string,
    params: GetOneParams,
  ): Promise<GetOneResult<T>> {
    if (FLAT_OPTION_RESOURCES.has(resource)) {
      const raw = await request<T>(`${PMS_API_ENDPOINT}/option/${toPostType(resource)}`);
      return { data: raw as T };
    }
    if (OPTION_RESOURCES.has(resource)) {
      const raw = await request<T>(`${PMS_API_ENDPOINT}/option/business_setting`);
      return { data: raw as T };
    }
    const postType = toPostType(resource);
    const url = `${PMS_API_ENDPOINT}/post/${params.id}?type=${postType}`;
    const raw = await request<Record<string, unknown>>(url);
    return { data: mapWpPostToRecord(raw) as T };
  },

  async create<T = Record<string, unknown>>(
    resource: string,
    params: CreateParams,
  ): Promise<CreateResult<T>> {
    // Flat option resources — POST the record directly, no meta wrapper
    if (FLAT_OPTION_RESOURCES.has(resource)) {
      const raw = await request<T>(`${PMS_API_ENDPOINT}/option/${toPostType(resource)}`, {
        method: 'POST',
        body: JSON.stringify(params.data),
      });
      return { data: raw as T };
    }

    // Standard option resources
    if (OPTION_RESOURCES.has(resource)) {
      const raw = await request<T>(`${PMS_API_ENDPOINT}/option/business_setting`, {
        method: 'POST',
        body: JSON.stringify(params.data),
      });
      return { data: raw as T };
    }

    const postType = toPostType(resource);
    const body = mapRecordToWpPost(params.data as Record<string, unknown>, postType);
    const raw = await request<Record<string, unknown>>(`${PMS_API_ENDPOINT}/post`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return { data: mapWpPostToRecord(raw) as T };
  },

  async update<T = Record<string, unknown>>(
    resource: string,
    params: UpdateParams,
  ): Promise<UpdateResult<T>> {
    // Flat option resources — PATCH/POST the record directly
    if (FLAT_OPTION_RESOURCES.has(resource)) {
      const raw = await request<T>(`${PMS_API_ENDPOINT}/option/${toPostType(resource)}`, {
        method: 'POST',
        body: JSON.stringify(params.data),
      });
      return { data: raw as T };
    }

    // Standard option resources
    if (OPTION_RESOURCES.has(resource)) {
      const raw = await request<T>(`${PMS_API_ENDPOINT}/option/business_setting`, {
        method: 'POST',
        body: JSON.stringify(params.data),
      });
      return { data: raw as T };
    }

    const postType = toPostType(resource);
    const body = mapRecordToWpPost(params.data as Record<string, unknown>, postType);
    const raw = await request<Record<string, unknown>>(`${PMS_API_ENDPOINT}/post/${params.id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return { data: mapWpPostToRecord(raw) as T };
  },

  async delete<T = Record<string, unknown>>(
    resource: string,
    params: DeleteParams,
  ): Promise<DeleteResult<T>> {
    const postType = toPostType(resource);
    const raw = await request<Record<string, unknown>>(
      `${PMS_API_ENDPOINT}/post/${params.id}?type=${postType}`,
      { method: 'DELETE' },
    );
    return { data: mapWpPostToRecord(raw) as T };
  },

  async deleteMany(
    resource: string,
    params: DeleteManyParams,
  ): Promise<DeleteManyResult> {
    const postType = toPostType(resource);
    let deleted = 0;
    for (const id of params.ids) {
      try {
        await request<Record<string, unknown>>(
          `${PMS_API_ENDPOINT}/post/${id}?type=${postType}`,
          { method: 'DELETE' },
        );
        deleted++;
      } catch (e) {
        console.warn(`Failed to delete ${resource} ${id}:`, e);
      }
    }
    return { deleted };
  },

  async updateMany<T = Record<string, unknown>>(
    resource: string,
    params: UpdateManyParams<T>,
  ): Promise<{ updated: number }> {
    const postType = toPostType(resource);
    const body = mapRecordToWpPost(params.data as Record<string, unknown>, postType);
    let updated = 0;
    for (const id of params.ids) {
      try {
        await request<Record<string, unknown>>(`${PMS_API_ENDPOINT}/post/${id}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });
        updated++;
      } catch (e) {
        console.warn(`Failed to update ${resource} ${id}:`, e);
      }
    }
    return { updated };
  },

  async getOption<T = unknown>(name: string): Promise<GetOptionResult<T>> {
    const raw = await request<T>(`${PMS_API_ENDPOINT}/option/${name}`);
    return { data: raw };
  },

  async setOption<T = unknown>(name: string, value: T): Promise<SetOptionResult<T>> {
    const raw = await request<T>(`${PMS_API_ENDPOINT}/option/${name}`, {
      method: 'POST',
      body: JSON.stringify(value),
    });
    return { data: raw };
  },
};
